/**
 * Device Communication Panel
 * Displays sent/received messages with timestamps and color coding
 */

import { MAX_MESSAGES } from 'methodscript-communication';
import type { ParsedError } from 'shared-monaco-methodscript';
import { parseErrorResponse, logger } from 'shared-monaco-methodscript';

export const MessageType = {
    Sent: 'sent',
    Received: 'received',
    Info: 'info',
    Error: 'error',
    Data: 'data',
} as const;

export type MessageType = (typeof MessageType)[keyof typeof MessageType];

type Message = {
    type: MessageType;
    content: string;
    timestamp: number;
    isClickable?: boolean;
    line?: number;
    column?: number;
};

export type NotificationLevel = 'info' | 'warn' | 'error';
export type NotificationCallback = (level: NotificationLevel, message: string) => void;

type TrackedListener = {
    target: EventTarget;
    type: string;
    listener: EventListener;
};

export class DeviceCommunicationPanel {
    private container: HTMLElement;
    private messages: Array<Message> = [];
    private lastRenderedIndex = 0;
    private isVisible = false;
    private parsePackagesEnabled = false;
    private editorNavigationCallback?: (line: number, column: number) => void;
    private notificationCallback?: NotificationCallback;
    /** Listeners attached to the toolbar/panel chrome. Removed on destroy(). */
    private panelListeners: Array<TrackedListener> = [];
    /** Listeners attached to individual message elements. Removed when the
     * message is pruned from the DOM or when destroy() is called. */
    private messageListeners: Array<TrackedListener> = [];

    constructor(containerId: string, editorNavigationCallback?: (line: number, column: number) => void, notificationCallback?: NotificationCallback) {
        const container = document.getElementById(containerId);
        if (!container) {
            throw new Error(`Container element with id '${containerId}' not found`);
        }
        this.container = container;
        this.editorNavigationCallback = editorNavigationCallback;
        this.notificationCallback = notificationCallback;
        this.initializePanel();
    }

    /**
     * Add an event listener and remember it so it can be removed later.
     */
    private addTrackedListener(bucket: Array<TrackedListener>, target: EventTarget, type: string, listener: EventListener): void {
        target.addEventListener(type, listener);
        bucket.push({ target, type, listener });
    }

    /**
     * Remove all tracked listeners in the given bucket.
     */
    private removeTrackedListeners(bucket: Array<TrackedListener>): void {
        for (const { target, type, listener } of bucket) {
            target.removeEventListener(type, listener);
        }
        bucket.length = 0;
    }

    /**
     * Remove tracked listeners whose target is a descendant of (or equal to)
     * the given element. Used when pruning message DOM nodes.
     */
    private removeListenersForElement(element: Element): void {
        this.messageListeners = this.messageListeners.filter(({ target, type, listener }) => {
            if (target === element || (target instanceof Node && element.contains(target))) {
                target.removeEventListener(type, listener);
                return false;
            }
            return true;
        });
    }

    /**
     * Initialize the panel HTML structure
     */
    private initializePanel(): void {
        this.container.innerHTML = `
            <div class="comm-panel">
                <div class="comm-toolbar">
                    <span class="comm-title">Device Communication</span>
                    <div class="comm-toolbar-actions">
                        <label class="comm-checkbox-label" title="Parse and display package data with variable types">
                            <input type="checkbox" id="commParsePackagesCheckbox" />
                            Parse Packages
                        </label>
                        <button id="commClearBtn" class="comm-btn" title="Clear messages">Clear</button>
                        <button id="commExportBtn" class="comm-btn" title="Export log">Export</button>
                        <button id="commCloseBtn" class="comm-btn-close" title="Close panel">×</button>
                    </div>
                </div>
                <div class="comm-messages" id="commMessages">
                    <div class="comm-empty">No messages yet. Connect to a device and send data.</div>
                </div>
            </div>
        `;

        // Set up button handlers
        const parsePackagesCheckbox = document.getElementById('commParsePackagesCheckbox') as HTMLInputElement;
        const clearBtn = document.getElementById('commClearBtn');
        const exportBtn = document.getElementById('commExportBtn');
        const closeBtn = document.getElementById('commCloseBtn');

        if (parsePackagesCheckbox) {
            this.addTrackedListener(this.panelListeners, parsePackagesCheckbox, 'change', () => {
                this.parsePackagesEnabled = parsePackagesCheckbox.checked;
            });
        }
        if (clearBtn) {
            this.addTrackedListener(this.panelListeners, clearBtn, 'click', () => this.clearMessages());
        }
        if (exportBtn) {
            this.addTrackedListener(this.panelListeners, exportBtn, 'click', () => this.exportLog());
        }
        if (closeBtn) {
            this.addTrackedListener(this.panelListeners, closeBtn, 'click', () => this.hide());
        }
    }

    /**
     * Show the communication panel
     */
    public show(): void {
        this.container.style.display = 'block';
        this.isVisible = true;
        this.scrollToBottom();
    }

    /**
     * Hide the communication panel
     */
    public hide(): void {
        this.container.style.display = 'none';
        this.isVisible = false;
    }

    /**
     * Toggle panel visibility
     */
    public toggle(): void {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    /**
     * Add a message to the panel
     */
    public addMessage(type: MessageType, content: string): void {
        const message: Message = {
            type,
            content,
            timestamp: Date.now(),
        };

        this.messages.push(message);

        // If this is a received message or error, check if it's an error response
        // and add a formatted error block as an additional message
        if (type === MessageType.Received || type === MessageType.Error) {
            const parsedError = parseErrorResponse(content);
            if (parsedError && parsedError.errorCode !== null) {
                this.addFormattedError(parsedError);
            }
        }

        // Keep only last MAX_MESSAGES messages
        const excess = this.messages.length - MAX_MESSAGES;
        if (excess > 0) {
            this.messages = this.messages.slice(-MAX_MESSAGES);
            this.removeOldestRenderedMessages(excess);
        }

        this.renderMessages();
        this.scrollToBottom();
    }

    /**
     * Add a formatted error message based on parsed error response
     */
    private addFormattedError(parsedError: ParsedError): void {
        // Build the formatted error message content
        let errorContent = '';

        if (parsedError.errorCode !== null) {
            const hexCode = parsedError.errorCode.toString(16).toUpperCase().padStart(4, '0');
            errorContent += `[0x${hexCode}]`;
        }

        if (parsedError.description) {
            errorContent += `: "${parsedError.description}"`;
        }

        // Show location if we have at least a line number
        if (parsedError.line !== null) {
            errorContent += ` Line ${parsedError.line}`;
            if (parsedError.column !== null) {
                errorContent += ` Col ${parsedError.column}`;
            }
        }

        // Error is clickable if we have at least a line number
        // If column is missing, we'll default to column 1 when navigating
        const isClickable = parsedError.line !== null;
        const column = parsedError.column ?? (parsedError.line !== null ? 1 : undefined);

        // Add as a new 'error' type message
        const errorMessage: Message = {
            type: MessageType.Error,
            content: errorContent,
            timestamp: Date.now(),
            isClickable,
            line: parsedError.line ?? undefined,
            column,
        };

        this.messages.push(errorMessage);
    }

    /**
     * Clear all messages
     */
    public clearMessages(): void {
        this.messages = [];
        this.lastRenderedIndex = 0;
        this.removeTrackedListeners(this.messageListeners);
        this.renderMessages();
    }

    /**
     * Remove the oldest rendered messages from the DOM when truncation occurs.
     */
    private removeOldestRenderedMessages(count: number): void {
        const messagesContainer = document.getElementById('commMessages');
        if (!messagesContainer) return;

        for (let i = 0; i < count && messagesContainer.firstChild; i++) {
            const child = messagesContainer.firstChild;
            if (child instanceof Element) {
                this.removeListenersForElement(child);
            }
            messagesContainer.removeChild(child);
        }
        this.lastRenderedIndex = Math.max(0, this.lastRenderedIndex - count);
    }

    /**
     * Render only new messages to the panel (append-only).
     * Previously rendered messages are kept in the DOM.
     */
    private renderMessages(): void {
        const messagesContainer = document.getElementById('commMessages');
        if (!messagesContainer) return;

        if (this.messages.length === 0) {
            messagesContainer.innerHTML = '<div class="comm-empty">No messages yet. Connect to a device and send data.</div>';
            this.lastRenderedIndex = 0;
            return;
        }

        // Remove empty state placeholder if this is the first message
        if (this.lastRenderedIndex === 0) {
            messagesContainer.innerHTML = '';
        }

        // Only render new messages (append-only)
        for (let i = this.lastRenderedIndex; i < this.messages.length; i++) {
            messagesContainer.appendChild(this.createMessageElement(this.messages[i]));
        }

        this.lastRenderedIndex = this.messages.length;
    }

    /**
     * Create a DOM element for a single message.
     */
    private createMessageElement(msg: Message): HTMLDivElement {
        const timestamp = this.formatTimestamp(msg.timestamp);
        const typeLabel = this.formatMessageType(msg.type);
        const escapedContent = this.escapeHtml(msg.content);

        const messageDiv = document.createElement('div');
        messageDiv.className = `comm-message comm-message-${msg.type}`;

        // If message is clickable (error with location), add clickable class and handler
        if (msg.isClickable && msg.line !== undefined && msg.column !== undefined) {
            messageDiv.classList.add('comm-message-clickable');
            messageDiv.style.cursor = 'pointer';
            messageDiv.title = `Click to navigate to Line ${msg.line}, Col ${msg.column}`;

            const { line } = msg;
            const { column } = msg;
            this.addTrackedListener(this.messageListeners, messageDiv, 'click', () => {
                if (this.editorNavigationCallback) {
                    this.editorNavigationCallback(line, column);
                }
            });
        } else {
            // For non-clickable messages, copy to clipboard on click
            this.addTrackedListener(this.messageListeners, messageDiv, 'click', () => {
                void navigator.clipboard.writeText(msg.content);
            });
        }

        messageDiv.innerHTML = `
            <span class="comm-message-timestamp">${timestamp}</span>
            <span class="comm-message-type">${typeLabel}</span>
            <span class="comm-message-content">${escapedContent}</span>
        `;

        return messageDiv;
    }

    /**
     * Format timestamp for display
     */
    private formatTimestamp(timestamp: number): string {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            fractionalSecondDigits: 3,
        } as Intl.DateTimeFormatOptions);
    }

    /**
     * Format message type for display
     */
    private formatMessageType(type: MessageType): string {
        return type.toUpperCase();
    }

    /**
     * Escape HTML to prevent XSS
     */
    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Scroll to bottom of messages
     */
    private scrollToBottom(): void {
        const messagesContainer = document.getElementById('commMessages');
        if (messagesContainer) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
    }

    /**
     * Export communication log to file
     */
    public exportLog(): void {
        if (this.messages.length === 0) {
            logger.warn('No messages to export');
            this.notificationCallback?.('info', 'No messages to export');
            return;
        }

        const logContent = this.messages
            .map((msg) => {
                const date = new Date(msg.timestamp).toISOString();
                const typeLabel = msg.type.toUpperCase().padEnd(8);
                return `[${date}] ${typeLabel} ${msg.content}`;
            })
            .join('\n');

        // Create a blob and download it
        const blob = new Blob([logContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `methodscript-log-${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * Check if package parsing is enabled
     */
    public isParsePackagesEnabled(): boolean {
        // Read directly from the checkbox element to ensure test compatibility
        const checkbox = document.getElementById('commParsePackagesCheckbox') as HTMLInputElement;
        return checkbox ? checkbox.checked : this.parsePackagesEnabled;
    }

    /**
     * Tear down the panel: remove all event listeners and clear the container.
     * Call this when the panel is no longer needed (e.g. on page teardown or
     * when recreating the panel) to avoid leaking listeners.
     */
    public destroy(): void {
        this.removeTrackedListeners(this.panelListeners);
        this.removeTrackedListeners(this.messageListeners);
        this.messages = [];
        this.lastRenderedIndex = 0;
        this.container.innerHTML = '';
    }
}
